import React from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';

const LANGUAGE_CODES = {
  fr: 'FR',
  fra: 'FR',
  fre: 'FR',
  french: 'FR',
  francais: 'FR',
  français: 'FR',
  en: 'ENG',
  eng: 'ENG',
  english: 'ENG',
  anglais: 'ENG',
  de: 'DE',
  deu: 'DE',
  ger: 'DE',
  german: 'DE',
  allemand: 'DE',
  it: 'IT',
  ita: 'IT',
  italian: 'IT',
  italien: 'IT',
  es: 'ESP',
  esp: 'ESP',
  spa: 'ESP',
  spanish: 'ESP',
  espagnol: 'ESP',
  ar: 'AR',
  ara: 'AR',
  arabic: 'AR',
  arabe: 'AR',
  pt: 'PT',
  por: 'PT',
  portuguese: 'PT',
  portugais: 'PT',
  ru: 'RU',
  rus: 'RU',
  russian: 'RU',
  russe: 'RU',
};

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string' && /[,;]/.test(value)) {
    return value.split(/[,;]+/).map((item) => item.trim()).filter(Boolean);
  }
  return [value];
};

const normalizeLanguageCode = (value) => {
  if (value === null || value === undefined) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const key = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (LANGUAGE_CODES[key]) return LANGUAGE_CODES[key];

  const safe = key.replace(/[^a-z]/g, '');
  if (!safe) return null;
  return safe.slice(0, Math.min(3, Math.max(2, safe.length))).toUpperCase();
};

const normalizeLanguageCodes = (therapist) => {
  const explicit = toList(therapist?.extra?.language_codes);
  const rawTags = therapist?.tags || {};
  const fallback = toList(rawTags.language ?? rawTags.languages ?? rawTags.langues);

  return [...explicit, ...fallback].reduce((codes, language) => {
    const code = normalizeLanguageCode(language);
    if (code && !codes.includes(code)) codes.push(code);
    return codes;
  }, []);
};

const formatRating = (rating) => {
  const parsed = Number(rating);
  const value = Number.isFinite(parsed) && parsed > 0 ? parsed : 5;
  const display = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return `${display}/5`;
};

const parseExperienceYears = (value, allowPlainNumber = false) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    if (!allowPlainNumber) return null;
    const years = Math.trunc(value);
    return years >= 0 && years <= 80 ? years : null;
  }

  const text = String(value).trim();
  if (allowPlainNumber && /^\d{1,2}$/.test(text)) {
    const years = Number(text);
    return years >= 0 && years <= 80 ? years : null;
  }

  const match = text.match(/\b(\d{1,2})\s*(?:\+)?\s*(?:ans?|annees?|années?|years?|yrs?|yr)(?:\s*(?:exp|experience|expérience))?\b/i);
  if (!match) return null;

  const years = Number(match[1]);
  return years >= 0 && years <= 80 ? years : null;
};

const getExperienceYears = (therapist) => {
  const extraYears = parseExperienceYears(therapist?.extra?.experience_years, true);
  if (extraYears !== null) return extraYears;

  const tags = therapist?.tags || {};
  for (const key of ['experience_years', 'experienceYears', 'years_experience']) {
    const parsed = parseExperienceYears(tags[key], true);
    if (parsed !== null) return parsed;
  }

  for (const experience of toList(tags.experience)) {
    const parsed = parseExperienceYears(experience);
    if (parsed !== null) return parsed;
  }

  return null;
};

const getCompletedBookingsCount = (therapist) => {
  const count = Number(therapist?.extra?.completed_bookings_count);
  return Number.isFinite(count) && count >= 0 ? Math.max(1, Math.trunc(count)) : null;
};

const getExperienceLabel = (years, isFrench) => {
  if (years === null) return null;
  if (isFrench) return `${years} ${years === 1 ? 'an' : 'ans'} exp`;
  return `${years} ${years === 1 ? 'yr' : 'yrs'} exp`;
};

const TreatmentCount = ({ count, isFrench }) => {
  if (count === null) return null;

  if (isFrench) {
    return <>{`${count} ${count === 1 ? 'soin' : 'soins'}`}</>;
  }

  return (
    <>
      {count}
      <span className="flypro-card-meta__count-label flypro-card-meta__count-label--full">
        {count === 1 ? '\u00A0treatment' : '\u00A0treatments'}
      </span>
      <span className="flypro-card-meta__count-label flypro-card-meta__count-label--short">
        {'\u00A0trt.'}
      </span>
    </>
  );
};

const FlyproCardMetadata = ({ therapist }) => {
  const { i18n } = useTranslation('therapist');
  const isFrench = i18n.language?.startsWith('fr');
  const rating = formatRating(therapist?.extra?.rating);
  const experienceYears = getExperienceYears(therapist);
  const completedBookingsCount = getCompletedBookingsCount(therapist);
  const languages = normalizeLanguageCodes(therapist);
  const visibleLanguages = languages.slice(0, 6);
  const hiddenLanguagesCount = Math.max(languages.length - visibleLanguages.length, 0);

  return (
    <div className="flypro-card-meta" aria-label="Flypro details">
      <p className="note flypro-card-meta__rating">
        <span className="flypro-card-meta__segment">{rating}</span>
        {experienceYears !== null && (
          <span className="flypro-card-meta__segment">
            {getExperienceLabel(experienceYears, isFrench)}
          </span>
        )}
        {completedBookingsCount !== null && (
          <span className="flypro-card-meta__segment">
            <TreatmentCount count={completedBookingsCount} isFrench={isFrench} />
          </span>
        )}
      </p>
      {visibleLanguages.length > 0 && (
        <p className="flypro-card-meta__languages">
          <LanguageIcon className="flypro-card-meta__language-icon" aria-hidden="true" />
          <span className="flypro-card-meta__language-text">
            {visibleLanguages.join(', ')}
            {hiddenLanguagesCount > 0 ? `, +${hiddenLanguagesCount}` : ''}
          </span>
        </p>
      )}
    </div>
  );
};

export default FlyproCardMetadata;
